import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress, Alert,
  Select, MenuItem, FormControl, InputLabel, Chip, LinearProgress,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, ReferenceLine,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { getMillingDashboard } from '../../../services/millingAnalyticsApi';

const KPI = ({ label, value, sub, color }: { label: string; value: any; sub?: string; color?: string }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 'bold', color: color || 'text.primary' }}>
        {value ?? '—'}
      </Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600, borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5 }}>
    {children}
  </Typography>
);

const MillingAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getMillingDashboard(days));
    } catch {
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress size={48} /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5">Milling Analytics</Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.period?.since} → today
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Period</InputLabel>
          <Select value={days} label="Period" onChange={(e) => setDays(Number(e.target.value))}>
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={14}>Last 14 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={60}>Last 60 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!data && !error && <Alert severity="info">No data available</Alert>}

      {data && <>
        {/* ── YIELD ── */}
        <SectionTitle>Milling Yield</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <KPI label="Avg Yield" value={data.yieldStats.avgYieldPct != null ? `${data.yieldStats.avgYieldPct}%` : '—'}
              color={data.yieldStats.avgYieldPct >= 75 ? 'success.main' : 'warning.main'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Min Yield" value={data.yieldStats.minYieldPct != null ? `${data.yieldStats.minYieldPct}%` : '—'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Max Yield" value={data.yieldStats.maxYieldPct != null ? `${data.yieldStats.maxYieldPct}%` : '—'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Orders Completed" value={data.yieldStats.completedOrders} sub={`of ${data.yieldStats.total} total`} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Total Input" value={`${data.yieldStats.totalInputMt} MT`} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Total Output" value={`${data.yieldStats.totalOutputMt} MT`} />
          </Grid>
        </Grid>

        {data.yieldTrend?.length > 0 && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Yield Trend (%)</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.yieldTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip formatter={(v: any) => `${v}%`} />
                  <ReferenceLine y={75} stroke="#ff9800" strokeDasharray="4 4" label={{ value: 'Target 75%', fontSize: 11 }} />
                  <Line type="monotone" dataKey="avgYield" stroke="#1976d2" strokeWidth={2} dot={false} name="Avg Yield" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* ── AFLATOXIN ── */}
        <SectionTitle>Aflatoxin Monitoring</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <KPI label="Pass Rate"
              value={data.aflatoxinStats.passRate != null ? `${data.aflatoxinStats.passRate}%` : '—'}
              color={data.aflatoxinStats.passRate >= 95 ? 'success.main' : 'error.main'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Avg Result" value={data.aflatoxinStats.avgPpb != null ? `${data.aflatoxinStats.avgPpb} ppb` : '—'}
              color={data.aflatoxinStats.avgPpb > 10 ? 'error.main' : 'success.main'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Max Result" value={data.aflatoxinStats.maxPpb != null ? `${data.aflatoxinStats.maxPpb} ppb` : '—'}
              color={data.aflatoxinStats.maxPpb > 10 ? 'error.main' : 'success.main'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Tests" value={data.aflatoxinStats.total}
              sub={`${data.aflatoxinStats.pass} pass / ${data.aflatoxinStats.fail} fail`} />
          </Grid>
        </Grid>

        {data.aflatoxinTrend?.length > 0 && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Aflatoxin Trend (ppb)</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.aflatoxinTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} unit=" ppb" />
                  <Tooltip formatter={(v: any) => `${v} ppb`} />
                  <ReferenceLine y={10} stroke="#f44336" strokeDasharray="4 4" label={{ value: 'Limit 10 ppb', fontSize: 11 }} />
                  <Line type="monotone" dataKey="avgPpb" stroke="#1976d2" strokeWidth={2} dot={false} name="Avg ppb" />
                  <Line type="monotone" dataKey="maxPpb" stroke="#f44336" strokeWidth={1} dot={false} name="Max ppb" strokeDasharray="3 3" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* ── FORTIFICATION ── */}
        <SectionTitle>Fortification</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <KPI label="Compliance Rate"
              value={data.fortStats.complianceRate != null ? `${data.fortStats.complianceRate}%` : '—'}
              color={data.fortStats.complianceRate >= 95 ? 'success.main' : 'error.main'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Compliant" value={data.fortStats.compliant} sub={`of ${data.fortStats.total} batches`} color="success.main" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Non-Compliant" value={data.fortStats.nonCompliant} color={data.fortStats.nonCompliant > 0 ? 'error.main' : 'text.primary'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Total Fortified" value={`${data.fortStats.totalBatchMt} MT`} />
          </Grid>
        </Grid>

        {/* ── DEGERMING ── */}
        <SectionTitle>Maize Degerming</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <KPI label="Avg Endosperm Yield"
              value={data.degermStats.avgEndospermYieldPct != null ? `${data.degermStats.avgEndospermYieldPct}%` : '—'}
              color={data.degermStats.avgEndospermYieldPct >= 75 ? 'success.main' : 'warning.main'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Avg Germ Yield"
              value={data.degermStats.avgGermYieldPct != null ? `${data.degermStats.avgGermYieldPct}%` : '—'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Total Batches" value={data.degermStats.total} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Total Input" value={`${data.degermStats.totalInputMt} MT`} />
          </Grid>
        </Grid>

        {/* ── SCHEDULE ── */}
        <SectionTitle>Schedule Adherence</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <KPI label="Adherence Rate"
              value={data.scheduleStats.adherenceRate != null ? `${data.scheduleStats.adherenceRate}%` : '—'}
              color={data.scheduleStats.adherenceRate >= 80 ? 'success.main' : 'warning.main'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Completed" value={data.scheduleStats.completed} color="success.main" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Postponed" value={data.scheduleStats.postponed} color={data.scheduleStats.postponed > 0 ? 'warning.main' : 'text.primary'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Scheduled / Running"
              value={`${data.scheduleStats.scheduled} / ${data.scheduleStats.running}`} />
          </Grid>
        </Grid>

        {data.scheduleStats.total > 0 && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Schedule Distribution</Typography>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart layout="vertical" data={[{
                  completed: data.scheduleStats.completed,
                  running: data.scheduleStats.running,
                  scheduled: data.scheduleStats.scheduled,
                  postponed: data.scheduleStats.postponed,
                  cancelled: data.scheduleStats.cancelled,
                }]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" hide />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completed" fill="#4caf50" name="Completed" stackId="a" />
                  <Bar dataKey="running" fill="#2196f3" name="Running" stackId="a" />
                  <Bar dataKey="scheduled" fill="#9e9e9e" name="Scheduled" stackId="a" />
                  <Bar dataKey="postponed" fill="#ff9800" name="Postponed" stackId="a" />
                  <Bar dataKey="cancelled" fill="#f44336" name="Cancelled" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* ── QUALITY ── */}
        <SectionTitle>Quality Tests</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <KPI label="Pass Rate"
              value={data.qualityStats.passRate != null ? `${data.qualityStats.passRate}%` : '—'}
              color={data.qualityStats.passRate >= 90 ? 'success.main' : 'warning.main'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Total Tests" value={data.qualityStats.total}
              sub={`${data.qualityStats.passed} passed / ${data.qualityStats.failed} failed`} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Avg Moisture" value={data.qualityStats.avgMoisture != null ? `${data.qualityStats.avgMoisture}%` : '—'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Avg Protein" value={data.qualityStats.avgProtein != null ? `${data.qualityStats.avgProtein}%` : '—'} />
          </Grid>
        </Grid>

        {/* ── GRAIN / SILOS ── */}
        <SectionTitle>Grain Stock & Silos</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <KPI label="Available Stock" value={`${data.grainStock.availableMt} MT`}
              color={data.grainStock.availableMt < 10 ? 'error.main' : 'text.primary'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Grain Lots" value={data.grainStock.totalLots} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Quarantine" value={`${data.grainStock.quarantineMt} MT`}
              color={data.grainStock.quarantineMt > 0 ? 'warning.main' : 'text.primary'} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KPI label="Silo Utilization" value={`${data.siloSummary.utilizationPct}%`}
              sub={`${data.siloSummary.totalStockMt} / ${data.siloSummary.totalCapacityMt} MT`} />
          </Grid>
        </Grid>
        {data.siloSummary.total > 0 && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1">Silo Utilization</Typography>
                {data.siloSummary.fullSilos > 0 && <Chip label={`${data.siloSummary.fullSilos} full`} size="small" color="warning" />}
                {data.siloSummary.lowSilos > 0 && <Chip label={`${data.siloSummary.lowSilos} low`} size="small" color="error" />}
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(data.siloSummary.utilizationPct, 100)}
                sx={{ height: 20, borderRadius: 2 }}
                color={data.siloSummary.utilizationPct > 90 ? 'warning' : 'primary'}
              />
              <Typography variant="caption" color="text.secondary">
                {data.siloSummary.utilizationPct}% of {data.siloSummary.totalCapacityMt} MT capacity
              </Typography>
            </CardContent>
          </Card>
        )}
      </>}
    </Box>
  );
};

export default MillingAnalytics;
